import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Loader2, CheckCircle, AlertCircle, ArrowLeft, Copy, Upload, FileJson, X, ChevronDown, ChevronRight, Zap, Clock, ExternalLink } from "lucide-react";
import Cookies from "js-cookie";
import Sidebar from "@/components/Sidebar";
import DashboardFooter from "@/components/DashboardFooter";
import { getSiteBrand } from "@/lib/siteHosts";
import { TIMEZONE_OPTIONS, getDetectedTimezone } from "@/pages/integrations/shared/timezoneUtils";
import TimezoneField from "@/pages/integrations/shared/TimezoneField";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleCalendarIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.316 5.684H24v12.632h-5.684V5.684zM5.684 24h12.632v-5.684H5.684V24zM18.316 5.684V0H5.684v5.684h12.632zM0 18.316h5.684V5.684H0v12.632zM5.684 24H0v-5.684h5.684V24zM18.316 24H24v-5.684h-5.684V24zM24 0h-5.684v5.684H24V0zM0 0v5.684h5.684V0H0z" />
  </svg>
);

const REDIRECT_URI_BASE = BACKEND_URL + "/api/auth/google/callback/";

const GoogleCalendar = () => {
  const navigate = useNavigate();

  // Integration state
  const [integrationId, setIntegrationId] = useState(null);
  const [authorizedRedirectURIs, setAuthorizedRedirectURIs] = useState(null);
  const [integrationData, setIntegrationData] = useState(null);
  const [isLoadingIntegration, setIsLoadingIntegration] = useState(true);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [hasExistingIntegration, setHasExistingIntegration] = useState(false);

  // File state
  const [jsonFile, setJsonFile] = useState(null);
  const [jsonFileName, setJsonFileName] = useState("");
  const [jsonFileContent, setJsonFileContent] = useState(null);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fileError, setFileError] = useState("");
  const [success, setSuccess] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [statusNotification, setStatusNotification] = useState(null);
  const [hasTokens, setHasTokens] = useState(false);
  const [isConnectionSettingsOpen, setIsConnectionSettingsOpen] = useState(true);

  // Test Connection modal state
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testStartTime, setTestStartTime] = useState("");
  const [testEndTime, setTestEndTime] = useState("");
  const [isCreatingTestEvent, setIsCreatingTestEvent] = useState(false);
  const [testResult, setTestResult] = useState(null); // { type: 'success' | 'error', message, event_id?, event_link? }

  const fileInputRef = useRef(null);

  const detectedTimezone = useMemo(() => getDetectedTimezone(), []);

  // Computed redirect URI with integration id appended
  const redirectUri = integrationId
    ? `${REDIRECT_URI_BASE}${integrationId}`
    : REDIRECT_URI_BASE;

  // Fetch or create Google Calendar integration on mount
  useEffect(() => {
    const fetchIntegration = async () => {
      try {
        const token = getClientAuthToken();
    if (!token) {
          setIsLoadingIntegration(false);
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/integrations/google_calendar`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setIntegrationId(data.id);
          setAuthorizedRedirectURIs(`${REDIRECT_URI_BASE}${data.id}`)
          setIntegrationData(data.integration);

          // Track whether tokens exist (connection is already authorized)
          const tokensExist = !!data.has_tokens;
          setHasTokens(tokensExist);
          // Collapse Connection Settings when tokens already exist
          setIsConnectionSettingsOpen(!tokensExist);

          // Populate integration status — only show status panel when credentials have been configured
          if (data.integration && data.integration.status) {
            const creds = data.integration.credentials || {};
            const hasCredentials = Object.keys(creds).length > 0 && (creds.web || creds.timezone);
            if (hasCredentials || tokensExist) {
              setIntegrationStatus(data.integration.status);
              setIsEnabled(data.integration.status !== "paused");
              setHasExistingIntegration(true);
            }
          }

          // Pre-fill timezone if previously saved (must be valid IANA key in options), otherwise auto-detect
          if (
            data.integration?.credentials?.timezone &&
            TIMEZONE_OPTIONS.some((tz) => tz.value === data.integration.credentials.timezone)
          ) {
            setSelectedTimezone(data.integration.credentials.timezone);
          } else {
            const detected = getDetectedTimezone();
            if (detected && TIMEZONE_OPTIONS.some((tz) => tz.value === detected)) {
              setSelectedTimezone(detected);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching Google Calendar integration:", error);
      } finally {
        setIsLoadingIntegration(false);
      }
    };

    fetchIntegration();
  }, []);

  // Auto-detect timezone on mount if no saved value
  useEffect(() => {
    if (!selectedTimezone && detectedTimezone) {
      if (TIMEZONE_OPTIONS.some((tz) => tz.value === detectedTimezone)) {
        setSelectedTimezone(detectedTimezone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedTimezone]);

  // Copy redirect URI to clipboard
  const handleCopyUri = async () => {
    try {
      await navigator.clipboard.writeText(redirectUri);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = redirectUri;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error("Copy failed", e);
      }
      document.body.removeChild(textArea);
    }
  };

  // Handle file reading — wrapped in useCallback so drag-and-drop
  // always sees the latest authorizedRedirectURIs value.
  const readFile = useCallback((file) => {
    setFileError("");

    if (!file) return;

    // Validate file type
    if (file.type !== "application/json" && !file.name.endsWith(".json")) {
      setFileError("Please upload a valid JSON file");
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      setFileError("File size must be less than 1MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = JSON.parse(e.target.result);

        // Validate structure: must have a "web" key
        if (!content.web || typeof content.web !== "object") {
          setFileError("Invalid credentials file. Expected a \"web\" application JSON from Google Cloud Console.");
          setJsonFile(null);
          setJsonFileName("");
          setJsonFileContent(null);
          return;
        }

        // Validate redirect_uris matches the Authorized redirect URI
        const fileRedirectUris = content.web.redirect_uris;
        console.log("fileRedirectUris: ", fileRedirectUris)
        console.log("authorizedRedirectURIs: ", authorizedRedirectURIs)
        if (!Array.isArray(fileRedirectUris) || !fileRedirectUris.includes(authorizedRedirectURIs)) {
          setFileError(
            `The redirect_uris in the uploaded JSON does not match the Authorized redirect URIs (${authorizedRedirectURIs}). Please update your Google Cloud Console credentials to include the correct redirect URI and re-download the JSON file.`
          );
          setJsonFile(null);
          setJsonFileName("");
          setJsonFileContent(null);
          return;
        }

        setJsonFile(file);
        setJsonFileName(file.name);
        setJsonFileContent(content);
        setFileError("");
      } catch (err) {
        setFileError("Invalid JSON file. Please check the file format.");
        setJsonFile(null);
        setJsonFileName("");
        setJsonFileContent(null);
      }
    };
    reader.onerror = () => {
      setFileError("Error reading file. Please try again.");
    };
    reader.readAsText(file);
  }, [authorizedRedirectURIs]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      readFile(files[0]);
    }
  }, [readFile]);

  // File input change handler
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      readFile(file);
    }
    // Reset input value so the same file can be selected again
    e.target.value = "";
  };

  // Remove uploaded file
  const handleRemoveFile = () => {
    setJsonFile(null);
    setJsonFileName("");
    setJsonFileContent(null);
    setFileError("");
  };

  // Handle form save — calls backend to persist credentials
  const handleSave = async () => {
    if (!jsonFileContent || !jsonFileContent.web) {
      setFileError("Please upload a valid JSON credentials file");
      return;
    }
    if (!selectedTimezone) {
      setFileError("Please select a time zone");
      return;
    }

    setIsSaving(true);
    setSuccess(false);
    setFileError("");

    try {
      const token = getClientAuthToken();
    if (!token) {
        setFileError("Authentication required. Please log in again.");
        setIsSaving(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/integrations/google_calendar/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          web: jsonFileContent.web,
          timezone: selectedTimezone,
        }),
      });

      if (response.ok) {
        const resData = await response.json();
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);

        // If the backend returned an authorization_url, redirect the user to Google OAuth
        if (resData.authorization_url) {
          window.location.href = resData.authorization_url;
        }
      } else {
        const errData = await response.json().catch(() => null);
        setFileError(errData?.detail || "Failed to save configuration. Please try again.");
      }
    } catch (error) {
      console.error("Error saving Google Calendar credentials:", error);
      setFileError("Network error. Please check your connection and try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!hasExistingIntegration) return;

    setIsTogglingStatus(true);
    setStatusNotification(null);

    try {
      const token = getClientAuthToken();
    if (!token) {
        setStatusNotification({ type: "error", message: "You are not logged in. Please login and try again." });
        setIsTogglingStatus(false);
        return;
      }

      const newStatus = isEnabled ? "paused" : "active";

      const response = await fetch(
        `${BACKEND_URL}/api/integrations/google_calendar/status?status=${newStatus}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.status === "ok") {
          setIsEnabled(!isEnabled);
          setIntegrationStatus(newStatus);

          const statusLabel = newStatus === "active" ? "enabled" : "disabled";
          setStatusNotification({
            type: "success",
            message: `Integration has been ${statusLabel} successfully`,
          });
          setTimeout(() => setStatusNotification(null), 3000);
        }
      } else {
        const errData = await response.json().catch(() => null);
        setStatusNotification({
          type: "error",
          message: errData?.detail || "Failed to update integration status.",
        });
        setTimeout(() => setStatusNotification(null), 5000);
      }
    } catch (err) {
      console.error("Toggle status error:", err);
      setStatusNotification({
        type: "error",
        message: "Network error. Please try again.",
      });
      setTimeout(() => setStatusNotification(null), 5000);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  // Open test modal with default times (now + 1 hour to now + 2 hours)
  const handleOpenTestModal = () => {
    const now = new Date();
    const startDate = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
    const endDate = new Date(now.getTime() + 2 * 60 * 60 * 1000); // 2 hours from now

    // Format as datetime-local value: YYYY-MM-DDTHH:MM
    const formatForInput = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    setTestStartTime(formatForInput(startDate));
    setTestEndTime(formatForInput(endDate));
    setTestResult(null);
    setIsTestModalOpen(true);
  };

  // Create test event
  const handleCreateTestEvent = async () => {
    if (!testStartTime || !testEndTime) return;

    // Validate end is after start
    if (new Date(testEndTime) <= new Date(testStartTime)) {
      setTestResult({ type: "error", message: "End time must be after start time." });
      return;
    }

    setIsCreatingTestEvent(true);
    setTestResult(null);

    try {
      const token = getClientAuthToken();
    if (!token) {
        setTestResult({ type: "error", message: "Authentication required. Please log in again." });
        setIsCreatingTestEvent(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/integrations/google_calendar/create_test_event`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          start_time: testStartTime,
          end_time: testEndTime,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setTestResult({
          type: "success",
          message: "Test event created successfully!",
          event_id: data.event_id,
          event_link: data.event_link,
        });
      } else {
        setTestResult({
          type: "error",
          message: data.detail || "Failed to create test event. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error creating test event:", error);
      setTestResult({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsCreatingTestEvent(false);
    }
  };

  const getStatusBadge = () => {
    if (!hasExistingIntegration) return null;

    const statusConfig = {
      active: {
        bg: "bg-green-500/10",
        border: "border-green-500/30",
        text: "text-green-400",
        label: "Active",
        icon: CheckCircle,
      },
      paused: {
        bg: "bg-yellow-500/10",
        border: "border-yellow-500/30",
        text: "text-yellow-400",
        label: "Paused",
        icon: AlertCircle,
      },
      error: {
        bg: "bg-red-500/10",
        border: "border-red-500/30",
        text: "text-red-400",
        label: "Error",
        icon: AlertCircle,
      },
      pending_auth: {
        bg: "bg-blue-500/10",
        border: "border-blue-500/30",
        text: "text-blue-400",
        label: "Pending Auth",
        icon: AlertCircle,
      },
    };

    const config = statusConfig[integrationStatus] || statusConfig.pending_auth;
    const Icon = config.icon;

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg} ${config.border} border`}
      >
        <Icon className={`w-4 h-4 ${config.text}`} />
        <span className={`text-sm font-medium ${config.text}`}>{config.label}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="google-calendar-integration-page">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Container */}
      <div className="flex-1 md:ml-64 flex flex-col">
        {/* Main Content */}
        <main className="flex-1 px-6 py-10">
          <div className="max-w-2xl mx-auto animate-fade-in-up">
            {/* Back Button */}
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-body">Back to Dashboard</span>
            </button>

            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[#4285F4]/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#4285F4]" />
                </div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6]">
                  Google Calendar Integration
                </h1>
              </div>
              <p className="text-[#9CA3AF] font-body text-sm">
                Connect your Google Calendar to enable automated scheduling
              </p>
            </div>

            {/* Integration Status Card */}
            {hasExistingIntegration && hasTokens && (
              <div className="glass rounded-2xl p-6 border border-white/10 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-[#F3F4F6] font-body text-sm">Current Status:</span>
                    {getStatusBadge()}
                  </div>

                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center gap-3">
                    <span className="text-[#9CA3AF] text-sm font-body">
                      {isEnabled ? "Enabled" : "Disabled"}
                    </span>
                    <button
                      onClick={handleToggleStatus}
                      disabled={isTogglingStatus}
                      className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                        isEnabled ? "bg-[#4285F4]" : "bg-white/20"
                      } ${isTogglingStatus ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid="status-toggle"
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform duration-300 ${
                          isEnabled ? "translate-x-7" : "translate-x-1"
                        }`}
                      />
                      {isTogglingStatus && (
                        <Loader2 className="w-3 h-3 animate-spin absolute top-1.5 left-1/2 -translate-x-1/2 text-[#0F1115]" />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Status Change Notification */}
                {statusNotification && (
                  <div
                    className={`mt-4 p-3 rounded-lg flex items-center gap-2 animate-fade-in-up ${
                      statusNotification.type === "success"
                        ? "bg-green-500/10 border border-green-500/30"
                        : "bg-red-500/10 border border-red-500/30"
                    }`}
                    data-testid="status-notification"
                  >
                    {statusNotification.type === "success" ? (
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm font-body ${
                        statusNotification.type === "success" ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {statusNotification.message}
                    </span>
                  </div>
                )}

                {/* Test Connection Button */}
                <div className="mt-5 pt-5 border-t border-white/10">
                  <button
                    type="button"
                    onClick={handleOpenTestModal}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#4285F4]/10 border border-[#4285F4]/30 text-[#4285F4] hover:bg-[#4285F4]/20 hover:border-[#4285F4]/50 transition-all duration-300 font-body text-sm font-medium"
                    data-testid="test-connection-button"
                  >
                    <Zap className="w-4 h-4" />
                    Test Connection
                  </button>
                  <p className="text-[#9CA3AF]/70 text-xs font-body mt-2">
                    Create a test event in your Google Calendar to verify the connection works
                  </p>
                </div>
              </div>
            )}

            {/* Configuration Form Card */}
            <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
              {/* Section Header — clickable toggle when tokens exist */}
              <div
                className={`${isConnectionSettingsOpen ? 'mb-8' : 'mb-0'} ${hasTokens ? 'cursor-pointer select-none' : ''}`}
                onClick={hasTokens ? () => setIsConnectionSettingsOpen((prev) => !prev) : undefined}
                data-testid="connection-settings-header"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-2">
                      Connection Settings
                    </h2>
                    <p className="text-[#9CA3AF] font-body text-sm">
                      Configure your Google Calendar API credentials
                    </p>
                  </div>
                  {hasTokens && (
                    <div className="flex items-center gap-2 text-[#9CA3AF]">
                      {isConnectionSettingsOpen ? (
                        <ChevronDown className="w-5 h-5 transition-transform duration-200" />
                      ) : (
                        <ChevronRight className="w-5 h-5 transition-transform duration-200" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Collapsible content */}
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isConnectionSettingsOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
                data-testid="connection-settings-content"
              >

              {/* Success Message */}
              {success && (
                <div
                  className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30"
                  data-testid="success-message"
                >
                  <p className="text-green-400 text-sm font-body text-center flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Configuration saved successfully!
                  </p>
                </div>
              )}

              <div className="space-y-6">
                {/* Authorized Redirect URIs */}
                <div className="space-y-2">
                  <label className="block text-[#F3F4F6] text-sm font-body">
                    Authorized redirect URIs
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={isLoadingIntegration ? "Loading..." : authorizedRedirectURIs}
                      readOnly
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#9CA3AF] font-body text-sm cursor-default focus:outline-none select-all"
                      data-testid="redirect-uri-input"
                    />
                    <button
                      type="button"
                      onClick={handleCopyUri}
                      className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all duration-300 font-body text-sm whitespace-nowrap ${
                        copied
                          ? "bg-green-500/10 border-green-500/30 text-green-400"
                          : "bg-white/5 border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20"
                      }`}
                      data-testid="copy-uri-button"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-[#9CA3AF]/70 text-xs font-body">
                    Add this URI to your Google Cloud Console OAuth 2.0 credentials
                  </p>
                </div>

                {/* JSON File Drop Zone */}
                <div className="space-y-2">
                  <label className="block text-[#F3F4F6] text-sm font-body">
                    Google Credentials JSON <span className="text-red-400">*</span>
                  </label>

                  {!jsonFile ? (
                    <div
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
                        isDragging
                          ? "border-[#4285F4] bg-[#4285F4]/10"
                          : fileError
                          ? "border-red-500/50 bg-red-500/5 hover:border-red-500/70"
                          : "border-white/15 bg-white/5 hover:border-[#4285F4]/50 hover:bg-[#4285F4]/5"
                      }`}
                      data-testid="json-drop-zone"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json,application/json"
                        onChange={handleFileInputChange}
                        className="hidden"
                        data-testid="json-file-input"
                      />
                      <div className="flex flex-col items-center gap-3">
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isDragging
                              ? "bg-[#4285F4]/20"
                              : "bg-white/10"
                          }`}
                        >
                          <Upload
                            className={`w-6 h-6 ${
                              isDragging ? "text-[#4285F4]" : "text-[#9CA3AF]"
                            }`}
                          />
                        </div>
                        <div>
                          <p className="text-[#F3F4F6] font-body text-sm mb-1">
                            {isDragging
                              ? "Drop your JSON file here"
                              : "Drag and drop your JSON file here"}
                          </p>
                          <p className="text-[#9CA3AF]/70 font-body text-xs">
                            or click to browse • JSON files only • Max 1MB
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="border border-white/10 rounded-xl p-4 bg-white/5"
                      data-testid="uploaded-file-info"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#4285F4]/10 flex items-center justify-center">
                            <FileJson className="w-5 h-5 text-[#4285F4]" />
                          </div>
                          <div>
                            <p className="text-[#F3F4F6] font-body text-sm font-medium">
                              {jsonFileName}
                            </p>
                            <p className="text-[#9CA3AF]/70 font-body text-xs">
                              {jsonFile.size < 1024
                                ? `${jsonFile.size} B`
                                : `${(jsonFile.size / 1024).toFixed(1)} KB`}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-red-400 hover:bg-red-500/10 transition-all"
                          data-testid="remove-file-button"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {fileError && (
                    <div className="flex items-center gap-2 mt-1">
                      <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                      <p className="text-red-400 text-xs font-body">{fileError}</p>
                    </div>
                  )}
                  <p className="text-[#9CA3AF]/70 text-xs font-body">
                    Upload the OAuth 2.0 Client ID JSON file downloaded from Google Cloud Console
                  </p>
                </div>

                <TimezoneField
                  value={selectedTimezone}
                  onChange={setSelectedTimezone}
                  variant="google"
                  detectedTimezone={detectedTimezone}
                  footerHintDefault="Select the time zone for your Google Calendar events"
                />

                {/* Save Button */}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!jsonFileContent || !selectedTimezone || isSaving}
                  className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 mt-4 ${
                    jsonFileContent && selectedTimezone && !isSaving
                      ? "btn-pulse bg-[#4285F4] text-white hover:bg-[#4285F4]/90"
                      : "bg-[#4285F4]/50 text-white/70 cursor-not-allowed"
                  }`}
                  data-testid="save-button"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Save
                    </>
                  )}
                </button>
              </div>
              </div>{/* end collapsible content */}
            </div>

            {/* Help Section */}
            <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10">
              <h3 className="font-heading font-semibold text-sm text-[#F3F4F6] mb-2">
                How to get your Google Calendar credentials
              </h3>
              <ol className="text-[#9CA3AF] text-xs font-body leading-relaxed space-y-1 list-decimal list-inside">
                <li>
                  Go to the{" "}
                  <a
                    href="https://console.cloud.google.com/apis/credentials"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4285F4] hover:underline"
                  >
                    Google Cloud Console
                  </a>
                </li>
                <li>Create or select an OAuth 2.0 Client ID</li>
                <li>Add the Authorized redirect URI shown above</li>
                <li>Download the JSON credentials file</li>
                <li>Upload the downloaded JSON file in the section above</li>
              </ol>
            </div>
          </div>
        </main>

        <DashboardFooter />
      </div>

      {/* Test Connection Modal */}
      {isTestModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="test-connection-modal"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              if (!isCreatingTestEvent) {
                setIsTestModalOpen(false);
                setTestResult(null);
              }
            }}
          />

          {/* Modal Content */}
          <div className="relative w-full max-w-md bg-[#1A1D23] rounded-2xl border border-white/10 shadow-2xl animate-fade-in-up">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#4285F4]/10 flex items-center justify-center">
                  <Zap className="w-4.5 h-4.5 text-[#4285F4]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">
                    Test Connection
                  </h3>
                  <p className="text-[#9CA3AF] text-xs font-body">
                    Create a test event in your calendar
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!isCreatingTestEvent) {
                    setIsTestModalOpen(false);
                    setTestResult(null);
                  }
                }}
                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/10 transition-all"
                disabled={isCreatingTestEvent}
                data-testid="close-test-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Event Title (read-only) */}
              <div className="space-y-2">
                <label className="block text-[#F3F4F6] text-sm font-body font-medium">
                  Event Title
                </label>
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                  <Calendar className="w-4 h-4 text-[#4285F4] flex-shrink-0" />
                  <span className="text-[#F3F4F6] font-body text-sm">{getSiteBrand().name} Test Event</span>
                </div>
              </div>

              {/* Start Time */}
              <div className="space-y-2">
                <label className="block text-[#F3F4F6] text-sm font-body font-medium">
                  Start Time <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
                  <input
                    type="datetime-local"
                    value={testStartTime}
                    onChange={(e) => setTestStartTime(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-[#F3F4F6] font-body text-sm focus:outline-none focus:border-[#4285F4]/50 focus:ring-1 focus:ring-[#4285F4]/30 transition-colors [color-scheme:dark]"
                    disabled={isCreatingTestEvent}
                    data-testid="test-start-time"
                  />
                </div>
              </div>

              {/* End Time */}
              <div className="space-y-2">
                <label className="block text-[#F3F4F6] text-sm font-body font-medium">
                  End Time <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
                  <input
                    type="datetime-local"
                    value={testEndTime}
                    onChange={(e) => setTestEndTime(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-[#F3F4F6] font-body text-sm focus:outline-none focus:border-[#4285F4]/50 focus:ring-1 focus:ring-[#4285F4]/30 transition-colors [color-scheme:dark]"
                    disabled={isCreatingTestEvent}
                    data-testid="test-end-time"
                  />
                </div>
              </div>

              {/* Result Message */}
              {testResult && (
                <div
                  className={`p-4 rounded-lg flex items-start gap-3 animate-fade-in-up ${
                    testResult.type === "success"
                      ? "bg-green-500/10 border border-green-500/30"
                      : "bg-red-500/10 border border-red-500/30"
                  }`}
                  data-testid="test-result-message"
                >
                  {testResult.type === "success" ? (
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-body font-medium ${
                        testResult.type === "success" ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {testResult.message}
                    </p>
                    {testResult.type === "success" && testResult.event_id && (
                      <p className="text-[#9CA3AF] text-xs font-body mt-1.5">
                        Event ID: <span className="text-[#F3F4F6] font-mono">{testResult.event_id}</span>
                      </p>
                    )}
                    {testResult.type === "success" && testResult.event_link && (
                      <a
                        href={testResult.event_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#4285F4] text-xs font-body mt-2 hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View event in Google Calendar
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => {
                  if (!isCreatingTestEvent) {
                    setIsTestModalOpen(false);
                    setTestResult(null);
                  }
                }}
                disabled={isCreatingTestEvent}
                className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20 transition-all font-body text-sm"
                data-testid="cancel-test-button"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateTestEvent}
                disabled={!testStartTime || !testEndTime || isCreatingTestEvent}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-body text-sm font-medium transition-all duration-300 ${
                  testStartTime && testEndTime && !isCreatingTestEvent
                    ? "bg-[#4285F4] text-white hover:bg-[#4285F4]/90"
                    : "bg-[#4285F4]/50 text-white/70 cursor-not-allowed"
                }`}
                data-testid="create-test-event-button"
              >
                {isCreatingTestEvent ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Create Test Event
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendar;
